import React, { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { COUNTRIES } from "@/lib/countries";
import { flagEmoji } from "@/lib/format";
import { cn } from "@/lib/utils";

// Searchable multi-select with checkboxes. Stays open on selection, shows a count in the
// trigger and an inline Clear (x) when anything is selected. `values` is an array of strings.
// Pass `country` to use the full ISO country list with flags instead of `options`.
export function MultiSelect({
  values = [], onChange, options = [], label = "Select", searchPlaceholder = "Search...",
  testId, widthClass = "w-[240px]", triggerClass, country = false,
}) {
  const [open, setOpen] = useState(false);
  const norm = country
    ? COUNTRIES.map((c) => ({ value: c.code, label: c.name, search: c.code, flag: c.code }))
    : options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const sel = new Set((values || []).map(String));
  const count = sel.size;
  const toggle = (v) => {
    const s = new Set((values || []).map(String));
    const key = String(v);
    s.has(key) ? s.delete(key) : s.add(key);
    onChange([...s]);
  };
  const clearAll = (e) => { e.stopPropagation(); e.preventDefault(); onChange([]); };
  const triggerLabel = count === 0 ? label : `${label} (${count})`;

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} role="combobox"
          className={cn("flex h-10 w-full items-center justify-between rounded-md border border-white/[0.1] bg-[#0A0A0A] px-3 text-sm text-foreground outline-none transition-colors hover:border-white/20 focus:border-white/25", triggerClass)}>
          <span className={cn("truncate", count === 0 && "text-muted-foreground")}>{triggerLabel}</span>
          <span className="ml-2 flex shrink-0 items-center gap-1">
            {count > 0 && (
              <span role="button" tabIndex={-1} data-testid={testId ? `${testId}-clear` : undefined} onClick={clearAll}
                className="rounded p-0.5 text-muted-foreground hover:bg-white/10 hover:text-foreground"><X className="h-3.5 w-3.5" /></span>
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn("border-white/[0.1] bg-[#1a1a1e] p-0", widthClass)} align="start">
        <Command className="bg-transparent" filter={(val, search, keywords) => {
          // Match on label + explicit search text only — never on internal UUIDs,
          // so typing digits matches public #IDs, not random id fragments.
          const hay = (keywords || []).join(" ").toLowerCase();
          return hay.includes(search.toLowerCase().trim()) ? 1 : 0;
        }}>
          <CommandInput placeholder={searchPlaceholder} className="text-sm" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {norm.map((o) => {
                const checked = sel.has(String(o.value));
                return (
                  <CommandItem key={String(o.value)} value={String(o.value)}
                    keywords={[o.label || "", o.search || ""]}
                    data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                    onSelect={() => toggle(o.value)}>
                    <span className={cn("mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded border", checked ? "border-white bg-white text-black" : "border-white/25")}>
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    {o.flag && <span className="mr-2 text-base leading-none">{flagEmoji(o.flag)}</span>}
                    <span className="flex-1 truncate">{o.label}</span>
                    {o.flag && <span className="ml-1 text-xs text-muted-foreground">{o.value}</span>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
